import styles from 'css/src/components/clip-creator/ClipCreatorMediaLibraryModal.module.scss';
import assetStyles from 'css/src/components/media-library/MediaLibraryAsset.module.scss';
import pickerStyles from 'css/src/components/media-library/UploadedMediaPickerModal.module.scss';
import voiceoverStyles from 'css/src/components/app/audio-tab/VoiceoverAsset.module.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import type { AssetContainerModel, AudioAssetModel, MediaFolder } from 'js/src/client';
import {
    ApiService,
    AssetContainerTypes,
    MediaSearchSourceEnum,
    SlideBackgroundModelTypeEnum,
    SortEnum,
} from 'js/src/client';
import AudioWaveform from 'js/src/components/app/audio-tab/AudioWaveform';
import PlayPauseButton from 'js/src/components/app/audio-tab/PlayPauseButton';
import SimpleAudioPlayer from 'js/src/components/app/audio-tab/SimpleAudioPlayer';
import { CurrentTimeMarker, DurationTimeMarker } from 'js/src/components/app/audio-tab/TimeMarkers';
import { ImageAsset, VideoAsset } from 'js/src/components/common/BackgroundAsset';
import { Toast } from 'js/src/components/common/Toast';
import { TertiaryButton } from 'js/src/components/common/ui/Button';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';
import { Tab, Tabs, TAB_SIZE, TAB_TITLE_POSITION } from 'js/src/components/common/ui/Tabs';
import type { TypeInputChangeEvent } from 'js/src/components/common/ui/TextInput';
import TextInput, { TEXT_INPUT_VARIANTS } from 'js/src/components/common/ui/TextInput';
import { MediaLibraryNoResults } from 'js/src/components/media-library/MediaLibraryNoResults';
import MediaLibrarySpinner from 'js/src/components/media-library/MediaLibrarySpinner';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS } from 'js/src/libs/consts';

// Folders are tagged with the search context that created them; these are the
// upload contexts (mirrors SEARCH_CONTEXT.UPLOADED_VISUALS/VOICEOVERS). Hardcoded
// here to keep this component free of the editor's media redux.
const UPLOADED_VISUALS_SOURCE = 'uploaded-visuals';

type MediaTab = 'video' | 'voiceover';

const MEDIA_TAB_ITEMS = [
    { key: 'video' as const, value: 'Media' },
    { key: 'voiceover' as const, value: 'Voiceovers' },
];

type Props = {
    showing: boolean;
    onMediaSelected: (mediaId: string, isVoiceover: boolean) => void;
    onHidden?: () => void;
};

function getAssetUuid(item: AssetContainerModel): string | null {
    if (item.type === AssetContainerTypes.AUDIO) {
        return item.audio?.uuid ?? null;
    }
    const background = item.background;
    if (background?.type === SlideBackgroundModelTypeEnum.VIDEO) {
        return background.video?.uuid ?? null;
    }
    return background?.image?.uuid ?? null;
}

function getAssetTitle(item: AssetContainerModel): string {
    if (item.type === AssetContainerTypes.AUDIO) {
        return item.audio?.title || item.audio?.name || 'Voiceover';
    }
    const background = item.background;
    if (background?.type === SlideBackgroundModelTypeEnum.VIDEO) {
        return background.video?.title || 'Video';
    }
    return background?.image?.title || 'Image';
}

/**
 * "Select uploaded media" picker for the chat attach-media flow (editor and
 * dashboard). Fetches uploads/folders directly from the API into local state and
 * renders its own tiles, so it touches none of the editor's media redux (no
 * `app`/`undoable` slices, no editor tile components) and runs on any store —
 * including the dashboard's. Reuses the shared modal shell, tab toggle, tiles, and
 * empty state for visual parity with the editor's own media library.
 */
const UploadedMediaPickerModal = ({ showing, onMediaSelected, onHidden }: Props) => {
    const [activeTab, setActiveTab] = useState<MediaTab>('video');
    const [searchTerm, setSearchTerm] = useState('');
    const [folders, setFolders] = useState<MediaFolder[]>([]);
    const [activeFolder, setActiveFolder] = useState<MediaFolder | null>(null);
    const [results, setResults] = useState<AssetContainerModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const isVoiceover = activeTab === 'voiceover';
    const showFolders = !isVoiceover && activeFolder === null && searchTerm.trim() === '';

    // Fetch one page of results for the current tab/folder/search. Appends when
    // paginating; replaces on a fresh (page 1) load.
    const fetchPage = useCallback(
        async (pageNum: number, append: boolean) => {
            if (append) {
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
            }
            try {
                let data;
                if (isVoiceover) {
                    const resp = await ApiService.audioRetrieve({
                        query: {
                            q: searchTerm,
                            page: pageNum,
                            count: 50,
                            search_source: 'voiceover_uploads',
                            autocorrect: false,
                            did_you_mean: false,
                            filter_by_user_level: false,
                        },
                    });
                    data = resp.data;
                } else {
                    const resp = await ApiService.mediaSearchCreate({
                        body: {
                            q: searchTerm,
                            page: pageNum,
                            search_source: MediaSearchSourceEnum.UPLOADS,
                            sort: SortEnum.LATEST,
                            folder_id: activeFolder?.id ?? null,
                        },
                    });
                    data = resp.data;
                }
                setResults((prev) => (append ? [...prev, ...data.results] : data.results));
                setHasMore(Boolean(data.next));
                setPage(pageNum);
            } catch {
                if (!append) {
                    setResults([]);
                    Toast.error('Something went wrong retrieving your uploads.');
                }
                setHasMore(false);
            }
            // Reset the loading flag after the try/catch (rather than in a `finally`, which
            // the React Compiler can't handle) — the catch swallows, so this always runs.
            if (append) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        },
        [isVoiceover, activeFolder, searchTerm],
    );

    // (Re)load the first page whenever the tab, folder or search term changes.
    useEffect(() => {
        if (!showing) {
            return;
        }
        void fetchPage(1, false);
    }, [showing, fetchPage]);

    // Infinite scroll: load the next page as the user nears the bottom.
    const onScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el || isLoading || isLoadingMore || !hasMore) {
            return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 400) {
            void fetchPage(page + 1, true);
        }
    }, [isLoading, isLoadingMore, hasMore, page, fetchPage]);

    // Fetch the (visual) upload folders once the picker opens.
    useEffect(() => {
        if (!showing) {
            return;
        }
        let cancelled = false;
        const loadFolders = async () => {
            try {
                const resp = await ApiService.mediaFoldersList();
                if (!cancelled) {
                    setFolders(
                        resp.data.filter(
                            (folder) =>
                                folder.creation_source === UPLOADED_VISUALS_SOURCE &&
                                folder.media_count > 0,
                        ),
                    );
                }
            } catch {
                if (!cancelled) {
                    setFolders([]);
                }
            }
        };
        void loadFolders();
        return () => {
            cancelled = true;
        };
    }, [showing]);

    // Reset transient state when the picker closes.
    useEffect(() => {
        if (showing) {
            return;
        }
        setActiveTab('video');
        setSearchTerm('');
        setActiveFolder(null);
        setResults([]);
        setPage(1);
        setHasMore(false);
    }, [showing]);

    // Clear the previous results (and show the spinner) immediately when switching tab or
    // folder, so the old tab's content doesn't linger until the new results load.
    const onTabChange = useCallback((tabKey: string) => {
        setActiveTab(tabKey as MediaTab);
        setActiveFolder(null);
        setSearchTerm('');
        setResults([]);
        setIsLoading(true);
    }, []);

    const onSearchChange = useCallback((e: TypeInputChangeEvent) => {
        setSearchTerm(e.target.value);
    }, []);

    const onFolderClick = useCallback((folder: MediaFolder) => {
        setActiveFolder(folder);
        setResults([]);
        setIsLoading(true);
    }, []);

    const onExitFolder = useCallback(() => {
        setActiveFolder(null);
        setResults([]);
        setIsLoading(true);
    }, []);

    const noResults = !isLoading && results.length === 0;

    const placeholder = useMemo(
        () =>
            activeFolder
                ? `Search ${activeFolder.name}`
                : `Search your uploaded ${isVoiceover ? 'voiceovers' : 'media'}...`,
        [activeFolder, isVoiceover],
    );

    return (
        <>
            <SimpleAudioPlayer />
            <Modal
                showing={showing}
                title="Select uploaded media"
                className={styles['clip-creator-media-library-modal']}
                bodyClassName="d-flex flex-column"
                onHidden={onHidden}
                size={MODAL_SIZES.LG}
                noPadding
                showFooterDivider={true}
                footer={
                    <div className="w-100 d-flex justify-content-end">
                        <TertiaryButton text="Cancel" onClick={onHidden} />
                    </div>
                }
            >
                <div className="px-l5-20 pt-l5-12">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(key) => onTabChange(key ?? 'video')}
                        titlePosition={TAB_TITLE_POSITION.START}
                        size={TAB_SIZE.SM}
                    >
                        {MEDIA_TAB_ITEMS.map((item) => (
                            <Tab key={item.key} eventKey={item.key} title={item.value} />
                        ))}
                    </Tabs>
                </div>
                <div className="px-l5-24 py-l5-16">
                    <TextInput
                        variant={TEXT_INPUT_VARIANTS.DEFAULT}
                        iconLeft="search"
                        placeholder={placeholder}
                        text={searchTerm}
                        onChange={onSearchChange}
                    />
                </div>
                <div
                    ref={scrollRef}
                    onScroll={onScroll}
                    className={classNames(
                        'scroll-container flex-grow-1 overflow-y-auto pb-l5-24',
                        pickerStyles['uploaded-media-picker'],
                    )}
                >
                    {activeFolder && (
                        <div className="px-l5-24 mb-l5-8">
                            <button
                                type="button"
                                className="btn btn-link p-l5-0 d-inline-flex align-items-center"
                                onClick={onExitFolder}
                            >
                                <span translate="no" className="material-symbols-outlined me-l5-4">
                                    arrow_back
                                </span>
                                {activeFolder.name}
                            </button>
                        </div>
                    )}
                    {showFolders && folders.length > 0 && (
                        <div className="container-fluid px-l5-24 mb-l5-16">
                            <div className="row row-cols-3 g-3">
                                {folders.map((folder) => (
                                    <div key={folder.id} className="col">
                                        <div
                                            className="card h-100 cursor-pointer d-flex flex-row align-items-center p-l5-8"
                                            onClick={() => onFolderClick(folder)}
                                        >
                                            <img
                                                src={folder.thumbnail_url}
                                                alt=""
                                                className={classNames(
                                                    'rounded me-l5-8',
                                                    pickerStyles['folder-thumbnail'],
                                                )}
                                                draggable={false}
                                            />
                                            <div className="text-truncate">
                                                <div className="font-l5 font-l5--subtitle-sm text-truncate">
                                                    {folder.name}
                                                </div>
                                                <div className="font-l5 font-l5--small color-grey-700">
                                                    {folder.media_count} item
                                                    {folder.media_count === 1 ? '' : 's'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {!noResults && (
                        <div className="container-fluid px-l5-24">
                            <div
                                className={classNames('row g-3', {
                                    'row-cols-3': !isVoiceover,
                                    'row-cols-2': isVoiceover,
                                })}
                            >
                                {results.map((item) => {
                                    const uuid = getAssetUuid(item);
                                    if (!uuid) {
                                        return null;
                                    }
                                    // Clicking a tile attaches it immediately (no separate
                                    // "select then confirm" step).
                                    const submit = () => onMediaSelected(uuid, isVoiceover);

                                    if (item.type === AssetContainerTypes.AUDIO && item.audio) {
                                        const media = ImmutableFromJS(
                                            item.audio,
                                        ) as ImmutableMap<AudioAssetModel>;
                                        return (
                                            <div key={uuid} className="col">
                                                <div
                                                    className={classNames(
                                                        assetStyles['media-library-asset'],
                                                        assetStyles['media-library-audio-asset'],
                                                    )}
                                                    onClick={submit}
                                                >
                                                    <div className="voiceover-asset position-relative">
                                                        <div
                                                            className={`${voiceoverStyles['waveform-container']} p-l5-8`}
                                                        >
                                                            <div className="position-relative">
                                                                <AudioWaveform media={media} />
                                                                <CurrentTimeMarker
                                                                    media={media}
                                                                    className="font-l5 font-l5--subtitle-l text-white"
                                                                />
                                                                <DurationTimeMarker
                                                                    media={media}
                                                                    className="font-l5 font-l5--subtitle-l text-white"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="details-container d-flex align-items-center p-l5-8">
                                                            <div className="play-pause-button-container">
                                                                <PlayPauseButton media={media} />
                                                            </div>
                                                            <div
                                                                className={`${voiceoverStyles['details']} flex-grow-1 d-flex px-l5-8`}
                                                            >
                                                                <span
                                                                    className="voiceover-name text-dark font-l5 font-l5--subtitle-l text-truncate"
                                                                    title={item.audio.name}
                                                                >
                                                                    {item.audio.name}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    const background = item.background;
                                    const video =
                                        background?.type === SlideBackgroundModelTypeEnum.VIDEO
                                            ? background.video
                                            : null;
                                    const image = background?.image ?? null;
                                    return (
                                        <div key={uuid} className="col">
                                            <div
                                                className={classNames(
                                                    assetStyles['media-library-asset'],
                                                    'position-relative w-100 h-100 cursor-pointer',
                                                )}
                                                onClick={submit}
                                                title={getAssetTitle(item)}
                                            >
                                                {video ? (
                                                    <VideoAsset
                                                        width={video.width}
                                                        height={video.height}
                                                        source={video.source}
                                                        posterUrl={video.poster_url}
                                                        thumbnailUrl={video.thumbnail_url}
                                                        duration={video.duration}
                                                        isEditorial={video.is_editorial}
                                                    />
                                                ) : image ? (
                                                    <ImageAsset
                                                        thumbnailUrl={image.thumbnail_url}
                                                        width={image.width}
                                                        height={image.height}
                                                        source={image.source}
                                                        isEditorial={image.is_editorial}
                                                    />
                                                ) : (
                                                    <div className="w-100 h-100 d-flex align-items-center justify-content-center p-l5-8 text-center font-l5 font-l5--small text-truncate">
                                                        {getAssetTitle(item)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {noResults && (
                        <MediaLibraryNoResults
                            className="my-l5-24"
                            isInFolder={activeFolder !== null}
                            isInSearch={searchTerm.trim() !== ''}
                            isVoiceover={isVoiceover}
                            emptyStateMessage="No media available"
                        />
                    )}
                    {(isLoading || isLoadingMore) && <MediaLibrarySpinner className="my-l5-24" />}
                </div>
            </Modal>
        </>
    );
};

export default UploadedMediaPickerModal;
